import * as React from 'react';
import { TrendingUp, TrendingDown, Minus, LucideIcon } from 'lucide-react';
import { cn, formatCompactNumber, formatCurrency } from '@/lib/utils';
import { Card } from '@/components/ui/card';

interface StatCardProps {
  title: string;
  value: number | string;
  change?: number;
  changeLabel?: string;
  subtitle?: string;
  icon?: LucideIcon;
  iconColor?: string;
  format?: 'number' | 'currency' | 'percentage' | 'none';
  className?: string;
  loading?: boolean;
  onClick?: () => void;
}

export function StatCard({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  subtitle,
  icon: Icon,
  iconColor = 'text-primary',
  format = 'number',
  className,
  loading = false,
  onClick,
}: StatCardProps) {
  const formatValue = () => {
    if (typeof value === 'string') return value;

    switch (format) {
      case 'currency':
        return formatCurrency(value);
      case 'percentage':
        return `${value}%`;
      case 'number':
        return formatCompactNumber(value);
      default:
        return value;
    }
  };

  const getTrendIcon = () => {
    if (change === undefined) return null;

    if (change > 0) {
      return <TrendingUp className="h-4 w-4 text-success" />;
    }
    if (change < 0) {
      return <TrendingDown className="h-4 w-4 text-destructive" />;
    }
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getTrendColor = () => {
    if (change === undefined) return '';
    if (change > 0) return 'text-success';
    if (change < 0) return 'text-destructive';
    return 'text-muted-foreground';
  };

  if (loading) {
    return (
      <Card className={cn('p-6', className)}>
        <div className="flex items-center justify-between">
          <div className="h-4 w-24 bg-muted animate-pulse rounded" />
          <div className="h-8 w-8 bg-muted animate-pulse rounded-full" />
        </div>
        <div className="mt-3 h-8 w-32 bg-muted animate-pulse rounded" />
        <div className="mt-2 h-4 w-20 bg-muted animate-pulse rounded" />
      </Card>
    );
  }

  return (
    <Card
      className={cn(
        'p-6',
        onClick && 'cursor-pointer transition-colors hover:border-primary/50 hover:bg-accent/40',
        className,
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        {Icon && (
          <div className={cn('rounded-full p-2 bg-muted', iconColor)}>
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
      <div className="mt-3">
        <h3 className="text-2xl font-bold">{formatValue()}</h3>
      </div>
      {subtitle && (
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      )}
      {change !== undefined && (
        <div className="mt-2 flex items-center gap-1 text-sm">
          {getTrendIcon()}
          <span className={cn('font-medium', getTrendColor())}>
            {Math.abs(change)}%
          </span>
          <span className="text-muted-foreground">{changeLabel}</span>
        </div>
      )}
    </Card>
  );
}

// Grid of stat cards
interface StatsGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}

export function StatsGrid({ children, columns = 4, className }: StatsGridProps) {
  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={cn('grid gap-4', gridCols[columns], className)}>
      {children}
    </div>
  );
}
